<template>
  <div class="col-span-2 rounded-3xl p-6 bg-white border flex flex-col h-full">
    <div class="text-2xl font-bold text-[#010302]">{{ title }}</div>
    <div class="py-1"></div>
    <div class="text-[#6D6D6D]">{{ description }}</div>
    <div class="py-3"></div>
    <div class="-mx-6 border-b border-[#E3E5E8]"></div>
    <div class="py-4"></div>
    <div>
      <div
        class="w-full cursor-pointer z-[1] relative"
        @click="
          () => {
            if (files.length < maxFiles) return
            $Events.dispatchEvent('toast_notification', {
              body: `Vous ne pouvez pas envoyer plus de ${maxFiles} fichiers`,
            })
          }
        "
      >
        <FileUploader
          :class="{ '!pointer-events-none ': files.length >= maxFiles }"
          :acceptedFilesTypes="acceptedFilesTypes"
          @onFileChangedV2="data => newFileSelected(data)"
          @uploadFailed="() => uploadFailed()"
          @uploading="data => onUploading(data)"
          :key="filePikerKey"
          exactTypeMatch
          :makeLoadingModal="false"
        >
          <div
            class="flex flex-col items-center cursor-pointer justify-center p-6 border-[0.2rem] #CCCDE3 border-dashed border-gray-100 w-full h-[30vh] md:h-[12vw] rounded-[1.5rem]"
          >
            <div class="w-12 h-12">
              <img src="@/assets/images/all/image.png" alt="" />
            </div>
            <div class="py-2"></div>
            <p class="font-bold text-lg text-center text-[#010302]">
              Glisser-déposer, ou
              <span
                class="text-primary !cursor-pointer !pointer-events-auto underline"
              >
                Parcourir
              </span>
            </p>
            <p class="text-[#6D6D6D] text-sm mt-1 text-center">
              jpeg, jpg, png ou pdf.
              <!-- <br /> -->
              Taille maximale 10 Mo par document.
            </p>
          </div>
        </FileUploader>
      </div>
    </div>
    <div class="py-6"></div>
    <div v-if="files && files[0]" class="text-[#010302]">Fichiers uploadés</div>
    <div class="py-3"></div>
    <div class="flex gap-6 flex-col-reverse divide-y-reverse divide-y">
      <div v-for="(file, i) in files" :key="i">
        <VDropdown
          :distance="6"
          theme="tooltip-light"
          placement="top"
          :triggers="isImage(file) && file.url ? ['hover', 'click'] : []"
          :popperTriggers="['hover']"
        >
          <div class="">
            <div
              class="relative flex gap-4 items-center duration-300 delay-300 pb-6"
              :class="{ 'opacity-50': file.isUploading }"
            >
              <div
                class="w-14 h-14 min-w-14 rounded-lg"
                :class="{ shimmerElementRoundedDefaultnone: file.isUploading }"
              >
                <img
                  :src="getFileImage(file)"
                  alt=""
                  class="w-full h-full object-cover object-center rounded-lg"
                />
                <div class="hidden" v-if="isImage(file) && file.url">
                  <img :src="file.url" alt="" />
                </div>
              </div>
              <div class="w-[35%] min-w-[35%]">
                <div class="w-full text-[#010302] truncate">
                  {{ file.name }}
                </div>
                <div class="text-sm text-[#6D6D6D]">
                  {{ file.type.replace(/.+\//, '') }} &bull;
                  {{ file.size }}
                </div>
              </div>
              <div
                :class="{ 'opacity-0': !file.isUploading }"
                class="duration-300 delay-500 w-full h-2 bg-slate-100 rounded-full overflow-hidden"
              >
                <div
                  class="h-full bg-primary duration-300 rounded-full"
                  :style="{
                    width: (!file.isUploading ? 100 : file.progress) + '%',
                  }"
                ></div>
              </div>

              <div class="ml-auto flex items-center gap-2">
                <slot
                  name="beforeTrash"
                  :file="file"
                  :index="i"
                  :stored="slotStoredData"
                  :updateStored="updateSlotStoredData"
                ></slot>
                <div
                  class="ml-auto cursor-pointer"
                  @click="removePicture(i, file)"
                >
                  <div class="text-xl" v-if="file.isUploading">&times;</div>
                  <div class="w-6 h-6" v-else>
                    <img src="@/assets/images/icons/trash.svg" alt="" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <template #popper>
            <div class="">
              <img
                :src="file.url"
                class="rounded-2xl w-full h-full object-contain"
                alt=""
              />
            </div>
          </template>
        </VDropdown>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import FileUploader from '@/components/utils/FileUploader.vue'
import pdfFileImage from '@/assets/images/files/pdf.png'
import pngFileImage from '@/assets/images/files/png.png'
import jpgFileImage from '@/assets/images/files/jpg.png'
import jpegFileImage from '@/assets/images/files/jpeg.png'

export default defineComponent({
  name: 'ProfileContent',
  components: {
    FileUploader,
  },
  props: {
    title: {
      type: String,
      default: 'Fichiers',
    },
    description: {
      type: String,
      default: 'Uploader vos fichiers',
    },
    maxFiles: {
      type: Number,
      default: 6,
    },
    minFiles: {
      type: Number,
      default: 1,
    },
    acceptedFiles: {
      type: Array,
      default() {
        return []
      },
    },
    defaultUrls: {
      type: Array,
      default() {
        return []
      },
    },
    notEnoughPicNotifTitle: {
      type: String,
      default: '{missingFilesCount} Fichiers manquants',
    },
    notEnoughPicNotifBody: {
      type: String,
      default: 'Veuillez compléter {missingFilesCount} Fichiers',
    },
  },
  data() {
    return {
      filePikerKey: 'none',
      step: 0,
      files: this.defaultUrls.map(url => ({
        id: url,
        name: this.$Commons.getFileNameFromUrl(url),
        url,
        type: this.$Commons.getMimeTypeFromUrl(url),
        size: '',
      })),
      fileTypes: {
        png: 'image/png',
        jpg: 'image/jpg',
        jpeg: 'image/jpeg',
        pdf: 'application/pdf',
      },
      fileImages: {
        pdf: pdfFileImage,
        png: pngFileImage,
        jpg: jpgFileImage,
        jpeg: jpegFileImage,
      },
      slotStoredData: {},
    }
  },
  mounted() {},
  beforeUnmount() {},
  methods: {
    updateSlotStoredData(newData) {
      this.slotStoredData = {
        ...this.slotStoredData,
        ...newData,
      }
    },
    getFileImage(file) {
      if (file.type == this.fileTypes.png) return this.fileImages.png
      if (file.type == this.fileTypes.pdf) return this.fileImages.pdf
      if (file.type == this.fileTypes.jpg) return this.fileImages.jpg
      if (file.type == this.fileTypes.jpeg && file.name.endsWith('.jpg'))
        return this.fileImages.jpg
      if (file.type == this.fileTypes.jpeg) return this.fileImages.jpeg
      return ''
    },
    isImage(file) {
      return /image/i.test(file.type)
    },
    newFileSelected({ id, name, url, type, size }) {
      if (this.files[this.files.length - 1]?.isUploading) {
        this.files[this.files.length - 1] = { id, name, url, type, size }
      } else {
        this.files.push({ id, name, url, type, size })
      }
      this.filePikerKey = `${Date.now()}`
      this.fileUploaderAvailable = false
      setTimeout(() => {
        this.fileUploaderAvailable = true
      }, 500)
    },
    uploadFailed() {
      if (this.files[this.files.length - 1]?.isUploading) {
        this.removePicture(this.files.length - 1)
      }
    },
    onUploading(data) {
      if (this.files[this.files.length - 1]?.isUploading) {
        this.files[this.files.length - 1] = { ...data, isUploading: true }
      } else {
        this.files.push({ ...data, isUploading: true })
      }
    },
    removePicture(index, pic) {
      if (pic?.isUploading) {
        this.filePikerKey = `${Date.now()}`
      }
      this.files = this.files.filter((file, i) => i !== index)
      this.$forceUpdate()
    },
    submit() {
      if (this.files[0] && this.files[0].isUploading) {
        this.$Events.dispatchEvent('toast_notification', {
          title: 'Upload en cours',
          body: "Veuillez patienter pendant l'upload du dernier fichier",
        })
        return
      }
      if (this.files.length < this.minFiles) {
        const missingFilesCount = this.minFiles - this.files.length
        this.$Events.dispatchEvent('toast_notification', {
          title: this.notEnoughPicNotifTitle.replace(
            /{missingFilesCount}/,
            missingFilesCount,
          ),
          body: this.notEnoughPicNotifBody.replace(
            /{missingFilesCount}/,
            missingFilesCount,
          ),
        })
        return
      }
      this.$emit('submit')
    },
    getData() {
      return {
        files: this.files,
        storedData: this.slotStoredData,
      }
    },
  },
  watch: {},
  computed: {
    acceptedFilesTypes() {
      const res = []
      this.acceptedFiles.map(accepted => {
        if (/image/i.test(accepted)) {
          res.push(this.fileTypes.png, this.fileTypes.jpg, this.fileTypes.jpeg)
        } else if (/png/i.test(accepted)) res.push(this.fileTypes.png)
        else if (/jpg/i.test(accepted)) res.push(this.fileTypes.jpg)
        else if (/jpeg/i.test(accepted)) res.push(this.fileTypes.jpeg)

        if (/pdf/i.test(accepted)) res.push(this.fileTypes.pdf)
      })
      return res
    },
  },
})
</script>
